// ============================================================
// CineSync — Unified API & WebSocket URL Configuration
// Resolves correct backend endpoints across:
//   - Local development (Vite port 3000 -> backend port 8787)
//   - Local LAN testing (e.g. mobile testing on 192.168.x.x)
//   - Production deployment (Cloudflare Pages -> Cloudflare Worker)
// ============================================================

/**
 * Returns the WebSocket base URL for signaling (e.g. ws://localhost:8787).
 * Rewrites localhost to the current hostname when accessing from mobile/LAN.
 */
export function getWebSocketBaseUrl(): string {
  const envUrl = (import.meta.env.VITE_WORKER_WS_URL as string)?.trim() || '';

  if (typeof window === 'undefined') {
    return envUrl || 'ws://localhost:8787';
  }

  // If VITE_WORKER_WS_URL is provided
  if (envUrl) {
    try {
      const parsed = new URL(envUrl);
      const isLoopback = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
      const isClientRemote = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';

      // If client is accessing from LAN IP or custom domain, rewrite loopback hostname to the client's host
      if (isLoopback && isClientRemote) {
        parsed.hostname = window.location.hostname;
        return parsed.toString().replace(/\/$/, '');
      }

      return envUrl.replace(/\/$/, '');
    } catch {
      return envUrl.replace(/\/$/, '');
    }
  }

  // Fallback: in dev, if frontend is on port 3000, backend signaling is on port 8787
  if (window.location.port === '3000') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.hostname}:8787`;
  }

  // Standard relative WebSocket on same origin
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

/**
 * Returns the HTTP base URL for backend API calls (e.g. http://localhost:8787).
 */
export function getApiBaseUrl(): string {
  const wsUrl = getWebSocketBaseUrl();

  if (wsUrl.startsWith('ws://')) {
    return wsUrl.replace('ws://', 'http://');
  }
  if (wsUrl.startsWith('wss://')) {
    return wsUrl.replace('wss://', 'https://');
  }

  if (typeof window !== 'undefined') {
    if (window.location.port === '3000') {
      return `${window.location.protocol}//${window.location.hostname}:8787`;
    }
    return window.location.origin;
  }

  return 'http://localhost:8787';
}

